import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { useAuth } from '@/hooks/auth/use-auth';
import { fetchLeaderboardData } from '@/lib/data/leaderboard';
import type { CompanyRanking, DepartmentRanking } from '@/types/leaderboard';

/**
 * Custom hook to fetch leaderboard data for a user
 *
 * Uses React Query for caching and state management.
 * Data is fetched via batched RPC for optimal performance (single round-trip).
 *
 * @param userId - User ID to fetch data for
 * @returns Object containing leaderboard data and loading state
 *
 * @example
 * const {
 *   departmentRankings,
 *   companyRankings,
 *   userDepartment,
 *   userCompanyName,
 *   isLoading
 * } = useLeaderboardData(user?.id);
 */
export const useLeaderboardData = (userId: string | undefined) => {
  const { session } = useAuth();

  const { data: leaderboardData, isLoading, isError, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.LEADERBOARD_DATA(userId ?? ''),
    queryFn: () => fetchLeaderboardData(userId!, session!),
    enabled: !!userId && !!session,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    structuralSharing: true,
    placeholderData: (previousData) => previousData,
  });

  // Extract data with defaults
  const departmentRankings = useMemo<DepartmentRanking[]>(() => {
    const rankings = leaderboardData?.department_rankings ?? [];
    const self = leaderboardData?.self_department;
    if (!self || rankings.some((ranking) => ranking.is_self)) return rankings;
    return [...rankings, { ...self, is_self: true }];
  }, [leaderboardData?.department_rankings, leaderboardData?.self_department]);

  const companyRankings = useMemo<CompanyRanking[]>(() => {
    const rankings = leaderboardData?.company_rankings ?? [];
    const self = leaderboardData?.self_company;
    if (!self || rankings.some((ranking) => ranking.is_self)) return rankings;
    return [...rankings, { ...self, is_self: true }];
  }, [leaderboardData?.company_rankings, leaderboardData?.self_company]);

  const userDepartment = useMemo<string | null>(
    () => leaderboardData?.viewer?.department ?? null,
    [leaderboardData?.viewer?.department],
  );

  const userCompanyName = useMemo<string | null>(
    () => leaderboardData?.viewer?.company_display_name ?? null,
    [leaderboardData?.viewer?.company_display_name],
  );

  return {
    departmentRankings,
    companyRankings,
    userDepartment,
    userCompanyName,
    contributes: leaderboardData?.contributes ?? false,
    minimumCohortSize: leaderboardData?.minimum_cohort_size ?? 5,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export { fetchLeaderboardData };
export type { DepartmentRanking, CompanyRanking };
